import React, { useCallback, useMemo, useState } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
} from 'react-native';
import type { TextStyle, ViewStyle } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  useWatchlist,
  useCaptureWatchlist,
  useDeleteWatchlist,
} from '../../services/queries/useWatchlist';
import { SymbolAutocomplete, SymbolOption } from '../../components/portfolio/SymbolAutocomplete';
import {
  getLocalizedInstrumentName,
  translateIndustry,
  translateSector,
} from '../../utils/translationHelpers';
import { showConfirm, showError } from '../../utils/alert';
import { usePortfolioNavigate } from '../../navigation/PortfolioQualifierContext';
import type { WatchlistItem } from '../../services/api/watchlistApi';

export function WatchlistScreen() {
  const { theme } = useTheme();
  const { language } = useLanguage();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);
  const navigateToPortfolio = usePortfolioNavigate();
  const { data, isLoading } = useWatchlist();
  const capture = useCaptureWatchlist();
  const remove = useDeleteWatchlist();
  const [symbol, setSymbol] = useState('');
  const [instrumentName, setInstrumentName] = useState<string | undefined>(undefined);

  const onAdd = async () => {
    const s = symbol.trim().toUpperCase();
    if (!s) return;
    try {
      const { item, current_price } = await capture.mutateAsync({ symbol: s });
      setSymbol('');
      setInstrumentName(undefined);
      navigateToPortfolio('AddWatchlistThesis', {
        watchlistItemId: item.id,
        symbol: item.symbol,
        instrumentName,
        currentPrice: current_price,
      });
    } catch (e) {
      showError((e as Error).message);
    }
  };

  const onSelect = (option: SymbolOption) => setInstrumentName(option.name);

  const localizedInstrumentName = (item: WatchlistItem) =>
    getLocalizedInstrumentName(
      item.instrument_name,
      item.instrument_name_sch,
      item.instrument_name_tch,
      language
    );

  const openThesis = (item: WatchlistItem) => {
    navigateToPortfolio('AddWatchlistThesis', {
      watchlistItemId: item.id,
      symbol: item.symbol,
      instrumentName: localizedInstrumentName(item) || undefined,
    });
  };

  const openTrade = (item: WatchlistItem) => {
    // Navigating with the watchlist symbol keeps the trade form's symbol
    // read-only: the backend migrates the watchlist thesis onto the new
    // position by (user, symbol) match, so the trade must stay on-symbol.
    navigateToPortfolio('AddTrade', {
      symbol: item.symbol,
      currency: item.instrument_currency ?? undefined,
      instrumentName: localizedInstrumentName(item) || undefined,
    });
  };

  const openInstrumentDetail = (item: WatchlistItem) => {
    navigateToPortfolio('InstrumentDetail', {
      symbol: item.symbol,
      currency: item.instrument_currency ?? 'USD',
      instrument_id: item.instrument_id,
    });
  };

  const removeItem = useCallback(
    (item: WatchlistItem) => async () => {
      const confirmed = await showConfirm(
        t('watchlist.removeTitle', { defaultValue: 'Remove from watchlist?' }),
        t('watchlist.removeMessage', {
          defaultValue: 'Remove {symbol} and its watchlist thesis?',
        }).replace('{symbol}', item.symbol),
        {
          confirmText: t('watchlist.remove', { defaultValue: 'Remove' }),
          cancelText: t('common.cancel', { defaultValue: 'Cancel' }),
          destructiveAction: true,
        }
      );
      if (confirmed) {
        remove.mutate(item.id);
      }
    },
    [remove, t]
  );

  return (
    <View style={styles.container}>
      <View style={styles.captureRow}>
        <View style={{ flex: 1 }}>
          <SymbolAutocomplete
            value={symbol}
            onChangeText={setSymbol}
            onSymbolSelect={onSelect}
            placeholder={t('watchlist.symbolPlaceholder')}
          />
        </View>
        <TouchableOpacity style={styles.addBtn} onPress={onAdd} disabled={capture.isPending}>
          <Text style={styles.addBtnText}>{t('watchlist.add')}</Text>
        </TouchableOpacity>
      </View>

      {isLoading ? (
        <ActivityIndicator color={theme.colors.primary} style={{ marginTop: theme.spacing.xl }} />
      ) : (
        <FlatList
          data={data ?? []}
          keyExtractor={(item) => String(item.id)}
          ListEmptyComponent={<Text style={styles.empty}>{t('watchlist.empty')}</Text>}
          ItemSeparatorComponent={() => <View style={styles.rowSeparator} />}
          renderItem={({ item, index }) => {
            const instrumentName = localizedInstrumentName(item);
            const sectorLabel = item.instrument_sector
              ? translateSector(item.instrument_sector)
              : '';
            const industryLabel = item.instrument_industry
              ? translateIndustry(item.instrument_industry)
              : '';

            return (
              <View style={[styles.row, index % 2 === 0 && styles.rowEven]}>
                <TouchableOpacity
                  style={styles.infoContainer}
                  onPress={() => openThesis(item)}
                  activeOpacity={0.7}
                  accessibilityRole="button"
                  accessibilityLabel={item.symbol}
                >
                  <Text style={styles.symbol}>{item.symbol}</Text>
                  {!!instrumentName && (
                    <Text style={[styles.instrumentName, { color: theme.colors.textSecondary }]}>
                      {instrumentName}
                    </Text>
                  )}
                  {(sectorLabel || industryLabel) && (
                    <Text style={[styles.metadata, { color: theme.colors.textTertiary }]}>
                      {sectorLabel && industryLabel
                        ? `${sectorLabel} / ${industryLabel}`
                        : sectorLabel || industryLabel}
                    </Text>
                  )}
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.thesisButton}
                  onPress={() => openThesis(item)}
                  activeOpacity={0.7}
                  accessibilityRole="button"
                  accessibilityLabel={`${item.symbol} ${t('watchlist.thesis')}`}
                >
                  <Text style={styles.thesisButtonText}>{t('watchlist.thesis')}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.tradeButton}
                  onPress={() => openTrade(item)}
                  activeOpacity={0.7}
                  accessibilityRole="button"
                  accessibilityLabel={`${item.symbol} ${t('watchlist.trade', { defaultValue: 'Trade' })}`}
                >
                  <Text style={styles.tradeButtonText}>
                    {t('watchlist.trade', { defaultValue: 'Trade' })}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.detailsButton}
                  onPress={() => openInstrumentDetail(item)}
                  activeOpacity={0.7}
                  accessibilityRole="button"
                  accessibilityLabel={`${item.symbol} ${t('portfolio.details', { defaultValue: 'Details' })}`}
                >
                  <Text style={styles.detailsButtonText}>
                    {t('portfolio.details', { defaultValue: 'Details' })}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.removeButton}
                  onPress={removeItem(item)}
                  accessibilityRole="button"
                  accessibilityLabel={`${item.symbol} ${t('watchlist.remove')}`}
                >
                  <Text style={styles.remove}>{t('watchlist.remove')}</Text>
                </TouchableOpacity>
              </View>
            );
          }}
        />
      )}
      <Text style={styles.disclaimer}>{t('common.advisoryDisclaimer')}</Text>
    </View>
  );
}

function createThemedStyles(theme: any) {
  const actionButton: ViewStyle = {
    backgroundColor: theme.colors.backgroundElevated,
    borderRadius: theme.radii.sm,
    borderWidth: theme.id === 'bauhaus' ? 2 : 1,
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: theme.spacing.xs,
    justifyContent: 'center',
    alignSelf: 'flex-start',
    marginTop: theme.spacing.xxs,
  };
  const actionButtonText: TextStyle = {
    fontSize: theme.typography.fontSizeXs,
    fontWeight: theme.typography.fontWeightSemibold,
  };

  return StyleSheet.create({
    container: { flex: 1, backgroundColor: theme.colors.background, padding: theme.spacing.lg },
    captureRow: { flexDirection: 'row', gap: theme.spacing.sm, alignItems: 'flex-start' },
    addBtn: {
      backgroundColor: theme.colors.primary,
      borderRadius: theme.radii.md,
      justifyContent: 'center',
      paddingHorizontal: theme.spacing.lg,
      height: 48,
      marginTop: 4,
    },
    addBtnText: { color: theme.colors.textInverse, fontWeight: theme.typography.fontWeightBold },
    row: {
      flexDirection: 'row',
      gap: theme.spacing.xs,
      paddingVertical: theme.spacing.sm,
      paddingHorizontal: theme.spacing.sm,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.border,
    },
    rowSeparator: { height: theme.spacing.sm },
    rowEven: { backgroundColor: theme.colors.surface },
    infoContainer: { flex: 1 },
    symbol: {
      color: theme.colors.text,
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    instrumentName: {
      fontSize: theme.typography.fontSizeXs,
      marginTop: theme.spacing.xxs,
    },
    metadata: {
      fontSize: theme.typography.fontSizeXs,
      marginTop: theme.spacing.xxs,
    },
    actionButton,
    thesisButton: {
      ...actionButton,
      borderColor: theme.colors.primary,
    },
    tradeButton: {
      ...actionButton,
      borderColor: theme.colors.success,
    },
    detailsButton: {
      ...actionButton,
      borderColor: theme.colors.info || theme.colors.primary,
    },
    removeButton: {
      ...actionButton,
      borderColor: theme.colors.error,
    },
    thesisButtonText: {
      ...actionButtonText,
      color: theme.colors.primary,
    },
    tradeButtonText: {
      ...actionButtonText,
      color: theme.colors.success,
    },
    detailsButtonText: {
      ...actionButtonText,
      color: theme.colors.info || theme.colors.primary,
    },
    remove: {
      ...actionButtonText,
      color: theme.colors.error,
    },
    empty: { color: theme.colors.textTertiary, textAlign: 'center', marginTop: theme.spacing.xl },
    disclaimer: {
      color: theme.colors.textTertiary,
      fontSize: theme.typography.fontSizeXs,
      textAlign: 'center',
      marginTop: theme.spacing.md,
    },
  });
}
